import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, Pencil, FileText, Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { usePolicy, usePolicyAssets } from "@/features/insurance/api";
import { PolicyForm } from "@/features/insurance/PolicyForm";
import { CoveredAssetsTab } from "@/features/insurance/CoveredAssetsTab";
import { EndorsementsTab } from "@/features/insurance/EndorsementsTab";
import { ClaimsTab } from "@/features/insurance/ClaimsTab";
import { CoverageRulesTab } from "@/features/insurance/CoverageRulesTab";
import { PolicyAuditTab } from "@/features/insurance/PolicyAuditTab";
import { PolicyStatusBadge, ExpiryBadge } from "@/features/insurance/badges";
import { daysToExpiry, formatINR, policyStatus } from "@/features/insurance/types";
import { ActionGuard } from "@/components/auth/ActionGuard";
import { useModuleAccess } from "@/hooks/useIsReadOnly";
import { useBranch } from "@/context/BranchContext";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { DocumentViewerDialog } from "@/components/documents/DocumentViewerDialog";
import { useConfigOptions } from "@/features/configuration/api";

const BRANCH_PRIVILEGED = new Set(["super_admin", "management"]);

export const Route = createFileRoute("/_authenticated/insurance/$policyId")({
  component: PolicyDetail,
});

function PolicyDetail() {
  const { policyId } = Route.useParams();
  const navigate = useNavigate();
  const { data: p, isLoading } = usePolicy(policyId);
  const { canWrite } = useModuleAccess("insurance");
  const { currentBranchId } = useBranch();
  const { data: me } = useCurrentUser();
  const roles = me?.roles ?? [];
  const isPrivileged = roles.some((r) => BRANCH_PRIVILEGED.has(r));
  // Fetch policy assets unfiltered so we can compute branch access for the
  // current user. List rendering filters again inside CoveredAssetsTab.
  const { data: allCovered = [], isLoading: caLoading } = usePolicyAssets(policyId);
  const [tab, setTab] = useState("overview");
  const [editing, setEditing] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);
  
  const { data: typeCfg } = useConfigOptions("insurance", "policy_type");

  if (isLoading || caLoading) return <div className="grid place-items-center p-10"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  if (!p) return <div className="p-6 text-sm text-muted-foreground">Policy not found.</div>;

  const isOwner = !!me?.user.id && p.owner_user_id === me.user.id;
  const hasBranchMatch = !currentBranchId
    ? true
    : allCovered.some(
        (r) => (r.asset as unknown as { branch_id?: string | null } | null | undefined)?.branch_id === currentBranchId,
      );
  const hasAccess = isPrivileged || isOwner || hasBranchMatch;

  if (!hasAccess) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate({ to: "/insurance" })}><ArrowLeft className="h-4 w-4" /></Button>
          <h1 className="text-xl font-semibold">Insurance</h1>
        </div>
        <Card>
          <CardContent className="space-y-2 p-6">
            <div className="flex items-center gap-2 text-amber-600">
              <ShieldAlert className="h-4 w-4" />
              <h2 className="text-lg font-semibold">Access denied</h2>
            </div>
            <p className="text-sm text-muted-foreground">You do not have access to this insurance record.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const d = daysToExpiry(p.end_date);
  const s = policyStatus(p);

  function handleViewDoc() {
    if (!p?.document_url) return;
    // Convert absolute localhost/127.0.0.1 media URLs to relative paths
    // so they load through the Vite proxy (avoids "refused to connect" in iframe)
    let url = p.document_url;
    try {
      const u = new URL(url);
      if ((u.hostname === "localhost" || u.hostname === "127.0.0.1") && u.pathname.startsWith("/media/")) {
        url = u.pathname + u.search;
      }
    } catch { /* ignore if not a valid URL */ }
    setResolvedUrl(url);
    setViewerOpen(true);
  }

  function handleViewerClose(open: boolean) {
    setViewerOpen(open);
    if (!open) setResolvedUrl(null);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate({ to: "/insurance" })}><ArrowLeft className="h-4 w-4" /></Button>
          <div>
            <h1 className="text-xl font-semibold">{p.policy_no}</h1>
            <p className="text-sm text-muted-foreground">
              {p.insurer_name} • {typeCfg?.find((t) => t.value === p.type)?.label ?? p.type}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <PolicyStatusBadge status={s} />
          <ExpiryBadge days={d} />
          <ActionGuard can={canWrite}>
            <Button size="sm" variant="outline" onClick={() => setEditing((e) => !e)}><Pencil className="mr-1 h-3 w-3" />{editing ? "Cancel" : "Edit"}</Button>
          </ActionGuard>
        </div>
      </div>

      {editing ? (
        <PolicyForm initial={p} onSaved={() => setEditing(false)} />
      ) : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="rules">Coverage Rules</TabsTrigger>
            <TabsTrigger value="assets">Covered Assets</TabsTrigger>
            <TabsTrigger value="endorsements">Endorsements</TabsTrigger>
            <TabsTrigger value="claims">Claims</TabsTrigger>
            <TabsTrigger value="renewal">Renewal History</TabsTrigger>
            <TabsTrigger value="audit">Audit</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-3">
            <Card><CardContent className="grid grid-cols-1 gap-4 p-4 md:grid-cols-2">
              <Field label="Broker" value={p.broker ?? "—"} />
              <Field label="Owner" value={p.owner?.full_name ?? p.owner?.email ?? "—"} />
              <Field label="Start Date" value={p.start_date} />
              <Field label="End Date" value={p.end_date} />
              <Field label="Sum Insured" value={formatINR(p.sum_insured)} />
              <Field label="Premium" value={formatINR(p.premium)} />
              <Field label="GST" value={formatINR(p.gst_amount)} />
              <Field label="Excess / Deductible" value={p.excess_amount != null ? formatINR(p.excess_amount) : "—"} />
              <Field label="Basis" value={p.basis ?? "—"} />
              <Field label="Payment Reference" value={p.payment_reference ?? "—"} />
              <Field label="Coverage Scope" value={p.coverage_scope ?? "—"} />
              <Field label="Coverage Mode" value={p.coverage_mode ?? "—"} />
              <Field label="Include Future Assets" value={p.include_future_assets ? "Yes" : "No"} />
              {p.property_details && Object.keys(p.property_details).length > 0 && (
                <div className="md:col-span-2">
                  <div className="text-xs text-muted-foreground">Property Details</div>
                  <pre className="mt-1 rounded bg-muted/40 p-2 text-xs">{JSON.stringify(p.property_details, null, 2)}</pre>
                </div>
              )}
              <div className="md:col-span-2"><Field label="Special Conditions" value={p.special_conditions ?? "—"} /></div>
              <div className="md:col-span-2"><Field label="Exclusions" value={p.exclusions_summary ?? "—"} /></div>
              {p.type === "Motor" && p.motor_addons ? (
                <div className="md:col-span-2"><Field label="Motor add-ons" value={JSON.stringify(p.motor_addons)} /></div>
              ) : null}
              <div className="md:col-span-2">
                <div className="text-xs text-muted-foreground">Policy Document</div>
                {p.document_url ? (
                  <>
                    <Button size="sm" variant="outline" onClick={handleViewDoc}>
                      <FileText className="mr-1 h-3 w-3" />
                      View / Download
                    </Button>
                    {resolvedUrl && viewerOpen && (
                      <DocumentViewerDialog
                        open={viewerOpen}
                        onOpenChange={handleViewerClose}
                        bucket="insurance-docs"
                        path={p.document_url}
                        overrideUrl={resolvedUrl}
                        fileName={p.document_url.split("/").pop() ?? "policy-document"}
                      />
                    )}
                  </>
                ) : <p className="text-sm text-muted-foreground">No policy document uploaded.</p>}
              </div>
            </CardContent></Card>
          </TabsContent>

          <TabsContent value="rules" className="mt-3"><CoverageRulesTab policyId={p.id} /></TabsContent>
          <TabsContent value="assets" className="mt-3"><CoveredAssetsTab policyId={p.id} /></TabsContent>
          <TabsContent value="endorsements" className="mt-3"><EndorsementsTab policyId={p.id} /></TabsContent>
          <TabsContent value="claims" className="mt-3"><ClaimsTab policyId={p.id} /></TabsContent>
          <TabsContent value="renewal" className="mt-3">
            <Card><CardContent className="p-6 text-sm text-muted-foreground">
              Renewal history will appear here as the policy is renewed. Use{" "}
              <Link to="/insurance/new" className="text-primary underline">New Policy</Link>{" "}
              and reference policy number <span className="font-mono">{p.policy_no}</span> to create the next term.
            </CardContent></Card>
          </TabsContent>
          <TabsContent value="audit" className="mt-3"><PolicyAuditTab policyId={p.id} /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-sm">{value}</div>
    </div>
  );
}
